"use client";

import type {WorkflowEditorBinding} from "@/contexts/chat/WorkflowEditorBridgeContext";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import type {WorkflowDebugAction, WorkflowRecovery} from "@/hooks/useWorkflowTurns";
import type {ConversationMessage} from "@/services/wingman";
import type {OmegaPresentation} from "@/services/wingman/omega-results";
import CompiledWorkflowPanel from "./CompiledWorkflowPanel";
import {useChatWorkflowPublication} from "./useChatWorkflowPublication";
import {useWorkflowEditorSync} from "./useWorkflowEditorSync";

type Props = {
   conversationId: string;
   messages: ConversationMessage[];
   presentation: OmegaPresentation;
   /** Whether `messages` holds the loaded history yet. */
   historyLoaded: boolean;
   /** The builder open beside this chat; its workflow is the one the chat edits. */
   editor: WorkflowEditorBinding | null;
   selectedWorkflowId?: string;
   onSelectWorkflow: (id: string) => void;
   assistantId?: string;
   /** The composer is disabled for a reason other than a running turn. */
   disabled: boolean;
   busy: boolean;
   recovery: WorkflowRecovery;
   generationError: string | null;
   onRecover: () => Promise<void>;
   onTest: (id: string) => Promise<boolean>;
   onDebug: (sessionId: string, action: WorkflowDebugAction) => Promise<boolean>;
};

/**
 * The workflow side of a Wingman v2 chat: inline saved workflows and
 * runs, publication from its cards, and the builder beside the chat kept in
 * step with the drafts the conversation saves.
 */
export default function ChatWorkflowPanel({editor, ...props}: Props) {
   const {showConversationOnRight} = useGailDock();
   useWorkflowEditorSync({
      binding: editor,
      conversationId: props.conversationId,
      messages: props.messages,
      historyLoaded: props.historyLoaded,
      busy: props.busy,
   });
   const publishWorkflow = useChatWorkflowPublication({
      assistantId: props.assistantId,
      editor,
   });

   return (
      <CompiledWorkflowPanel
         messages={props.messages}
         presentation={props.presentation}
         selectedId={editor?.workflowId ?? props.selectedWorkflowId}
         onSelect={editor ? undefined : props.onSelectWorkflow}
         openInEditor={!editor}
         onOpenInEditor={() => showConversationOnRight(props.conversationId)}
         disabled={props.disabled}
         busy={props.busy}
         recovery={props.recovery}
         generationError={props.generationError}
         onRecover={props.onRecover}
         onTest={props.onTest}
         onDebug={props.onDebug}
         onPublish={publishWorkflow}
      />
   );
}
